import React, { useEffect, useState } from 'react';
import { PersonalParameterInput } from '../common/ParameterInput';
import { CareerList } from './CareerInput';
import { PersonData, PersonalParameterDefinition } from '../../types';
import { ParameterLabelWithTooltip } from '../common/Tooltip';

interface PersonalParametersProps {
  people: PersonData[];
  personalParameters: PersonalParameterDefinition[];
  onPersonChange: (personId: string, field: string, value: any) => void;
  onPersonDelete: (personId: string) => void;
  onPersonAdd: () => void;
  onCareerChange: (personId: string, careerId: string, field: string, value: any) => void;
  onCareerDelete: (personId: string, careerId: string) => void;
  onCareerAdd: (personId: string) => void;
  simStartDate: string;
  disabled?: boolean;
}

/**
 * Component for managing personal parameters for all people in the simulation
 */
export const PersonalParameters: React.FC<PersonalParametersProps> = ({
  people,
  personalParameters,
  onPersonChange,
  onPersonDelete,
  onPersonAdd,
  onCareerChange,
  onCareerDelete,
  onCareerAdd,
  simStartDate,
  disabled = false
}) => {
  const [calculatedFields, setCalculatedFields] = useState<Record<string, Record<string, any>>>({});
  const maxPeople = 2;

  // Calculate age based on birthdate and sim start date
  const calculateCurrentAge = (birthdateString: string, simStartDateString: string): number => {
    if (!birthdateString || !simStartDateString) return 0;
    
    try {
      const birthDate = new Date(birthdateString);
      const simStartDate = new Date(simStartDateString);
      let age = simStartDate.getFullYear() - birthDate.getFullYear();
      const m = simStartDate.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && simStartDate.getDate() < birthDate.getDate())) {
        age--;
      }
      return Math.max(0, age);
    } catch (e) {
      console.error('Error calculating age:', e);
      return 0;
    }
  };

  // Extract parameter values for dependency tracking
  const parameterValuesString = JSON.stringify(people.map(p => p.parameterValues));

  // Update calculated fields when dependencies change
  useEffect(() => {
    const newCalculatedFields: Record<string, Record<string, any>> = {};

    people.forEach(person => {
      newCalculatedFields[person.id] = {};

      personalParameters.forEach(param => {
        if (param.type === 'calculated') {
          switch (param.id) {
            case 'currentAge':
              const birthdateParam = person.parameterValues.birthdate;
              const birthdate = (birthdateParam && typeof birthdateParam === 'object' && birthdateParam !== null && 'value' in birthdateParam) 
                ? birthdateParam.value as string : '';
              newCalculatedFields[person.id][param.id] = calculateCurrentAge(birthdate, simStartDate);
              break;

            case 'socialSecurityAnnualBenefit':
              const monthlyBenefitParam = person.parameterValues.socialSecurityMonthlyBenefit;
              const monthlyBenefit = (monthlyBenefitParam && typeof monthlyBenefitParam === 'object' && monthlyBenefitParam !== null && 'value' in monthlyBenefitParam) 
                ? monthlyBenefitParam.value as number : 0;
              const annualBenefit = monthlyBenefit * 12;
              newCalculatedFields[person.id][param.id] = annualBenefit.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              });
              break;

            case 'yearsInRetirement':
              const retirementAgeParam = person.parameterValues.retirementAge;
              const retirementAge = (retirementAgeParam && typeof retirementAgeParam === 'object' && retirementAgeParam !== null && 'value' in retirementAgeParam) 
                ? retirementAgeParam.value as number : 0;
              const lifeExpectancy = person.parameterValues.lifeExpectancy;
              if (lifeExpectancy && typeof lifeExpectancy === 'object' && 'mean' in lifeExpectancy) {
                const yearsInRetirement = Math.max(0, lifeExpectancy.mean - retirementAge);
                const stdDev = lifeExpectancy.stdDev || 0;
                if (stdDev > 0) {
                  const minYears = Math.max(0, yearsInRetirement - stdDev);
                  const maxYears = yearsInRetirement + stdDev;
                  newCalculatedFields[person.id][param.id] = `about ${minYears.toFixed(0)} – ${maxYears.toFixed(0)}`;
                } else {
                  newCalculatedFields[person.id][param.id] = yearsInRetirement.toFixed(0);
                }
              } else {
                newCalculatedFields[person.id][param.id] = 'N/A';
              }
              break;

            default:
              newCalculatedFields[person.id][param.id] = 'N/A';
          }
        }
      });
    });

    setCalculatedFields(newCalculatedFields);
  }, [people, personalParameters, simStartDate, parameterValuesString]);

  const handlePersonNameChange = (personId: string, name: string) => {
    onPersonChange(personId, 'name', name);
  };

  const handleParameterChange = (personId: string, parameterId: string, value: any) => {
    const paramDef = personalParameters.find(p => p.id === parameterId);
    if (!paramDef) return;

    let structuredValue;
    if (paramDef.type === 'date') {
      structuredValue = { value, type: 'date' as const };
    } else if (paramDef.type === 'number') {
      structuredValue = { value, type: 'number' as const };
    } else if (paramDef.type === 'meanStdDev') {
      structuredValue = { ...value, type: 'meanStdDev' as const };
    } else {
      structuredValue = value;
    }

    onPersonChange(personId, `parameterValues.${parameterId}`, structuredValue);
  };

  const getParameterValue = (person: PersonData, parameter: PersonalParameterDefinition) => {
    if (parameter.type === 'calculated') {
      return calculatedFields[person.id]?.[parameter.id] || 'Calculating...';
    }
    
    const paramValue = person.parameterValues[parameter.id];
    if (!paramValue) return undefined;
    
    // Extract the actual value based on parameter type
    if (parameter.type === 'date' || parameter.type === 'number') {
      return (typeof paramValue === 'object' && paramValue !== null && 'value' in paramValue) 
        ? paramValue.value 
        : paramValue;
    } else if (parameter.type === 'meanStdDev') {
      return (typeof paramValue === 'object' && paramValue !== null && 'mean' in paramValue) 
        ? { mean: paramValue.mean, stdDev: paramValue.stdDev } 
        : paramValue;
    }
    
    return paramValue;
  };

  return (
    <div className="personal-parameters">
        {/* Header Row */}
        <div className="personal-params-header">
          <div>Parameter</div>
          <div>
            {people[0] && (
              <>
                <input
                  type="text"
                  value={people[0].name}
                  onChange={(e) => handlePersonNameChange(people[0].id, e.target.value)}
                  disabled={disabled}
                  className="person-name-input"
                />
                {people.length > 1 && (
                  <button
                    type="button"
                    onClick={() => onPersonDelete(people[0].id)}
                    className="delete-person-btn"
                    title={`Delete ${people[0].name}`}
                    disabled={disabled}
                  >
                    🗑️
                  </button>
                )}
              </>
            )}
          </div>
          <div>
            {people[1] && (
              <>
                <input
                  type="text"
                  value={people[1].name}
                  onChange={(e) => handlePersonNameChange(people[1].id, e.target.value)}
                  disabled={disabled}
                  className="person-name-input"
                />
                <button
                  type="button"
                  onClick={() => onPersonDelete(people[1].id)}
                  className="delete-person-btn"
                  title={`Delete ${people[1].name}`}
                  disabled={disabled}
                >
                  🗑️
                </button>
              </>
            )}
            {people.length < maxPeople && !people[1] && (
              <button
                type="button"
                onClick={onPersonAdd}
                className="add-person-btn"
                disabled={disabled}
              >
                + Add Person
              </button>
            )}
          </div>
          <div></div>
        </div>

        {/* Parameter Rows */}
        {personalParameters.map((parameter) => (
          <div key={parameter.id} className="personal-param-row">
            <div className="param-label">
              {parameter.description ? (
                <ParameterLabelWithTooltip
                  label={parameter.name}
                  description={parameter.description}
                />
              ) : (
                <span>{parameter.name}:</span>
              )}
            </div>
            <div className="param-value">
              {people[0] && (
                <PersonalParameterInput
                  parameter={parameter}
                  value={getParameterValue(people[0], parameter)}
                  onChange={(id, value) => handleParameterChange(people[0].id, id, value)}
                  disabled={disabled}
                  personId={people[0].id}
                />
              )}
            </div>
            <div className="param-value">
              {people[1] && (
                <PersonalParameterInput
                  parameter={parameter}
                  value={getParameterValue(people[1], parameter)}
                  onChange={(id, value) => handleParameterChange(people[1].id, id, value)}
                  disabled={disabled}
                  personId={people[1].id}
                />
              )}
            </div>
            <div></div>
          </div>
        ))}

        {/* Careers Row */}
        <div className="careers-row">
          <div className="param-label">
            <ParameterLabelWithTooltip
              label="Careers"
              description="For each person, this is a list of zero or more careers that you anticipate holding. You can estimate when they might start and stop and how much each will contribute to your retirement account."
            />
          </div>
          <div className="param-value">
            {people[0] && (
              <CareerList
                careers={people[0].careers}
                person={people[0]}
                onCareerChange={(careerId, field, value) => onCareerChange(people[0].id, careerId, field, value)}
                onCareerDelete={(careerId) => onCareerDelete(people[0].id, careerId)}
                onCareerAdd={() => onCareerAdd(people[0].id)}
                simStartDate={simStartDate}
              />
            )}
          </div>
          <div className="param-value">
            {people[1] && (
              <CareerList
                careers={people[1].careers}
                person={people[1]}
                onCareerChange={(careerId, field, value) => onCareerChange(people[1].id, careerId, field, value)}
                onCareerDelete={(careerId) => onCareerDelete(people[1].id, careerId)}
                onCareerAdd={() => onCareerAdd(people[1].id)}
                simStartDate={simStartDate}
              />
            )}
          </div>
          <div></div>
        </div>
    </div>
  );
};
